import React, { useEffect, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import { Button, Card, CardBody, CardTitle, Col, Label, Row } from "reactstrap"
import { Formik, Form, Field, ErrorMessage } from "lib/formik"
import { useLocation, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import Spinners from "components/Shared/Spinners"
import { showAlert } from "store/reducers/alertReducer"
import BackButton from "components/Shared/BackButton"
import { editNotificationSchema } from "components/Common/validation"
import Switch from "components/Shared/Switch"
import {
  getPushNotificationDetails,
  updatePushNotification,
} from "store/actions/pushNotificationAction"
import TextError from "components/Shared/TextError"
import { useTranslation } from "react-i18next"
import { getSubPermissions } from "common/isPermissionEnabled"
import { isFulfillmentSubPermissionEnabled } from "utils/fulfillmentPermissions"

const initialValues = {
  titleEn: "",
  titleAr: "",
  bodyEn: "",
  bodyAr: "",
  remark: "",
  status: true,
  pickupTitleEn: "",
  pickupTitleAr: "",
  pickupBodyEn: "",
  pickupBodyAr: "",
}

const ORDER_STATUS_TYPE = "ORDER_STATUS"

// Optional text for pickup/curbside orders. Empty fields fall back to the
// default (delivery) text above them.
const PICKUP_FIELDS = [
  { name: "pickupTitleEn", label: "Title(English)" },
  { name: "pickupTitleAr", label: "Title(عربي)", rtl: true },
  { name: "pickupBodyEn", label: "Message(English)", textarea: true },
  { name: "pickupBodyAr", label: "Message(عربي)", textarea: true, rtl: true },
]

const EditAppNotification = () => {
  const { t } = useTranslation()
  document.title = t("Edit Notification ")

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { loading, isLoading, pushNotificationDetails } = useSelector(
    state => state.PushNotifications
  )

  const [formValues, setFormValues] = useState(initialValues)
  const { userRole, accountType, accountScope } = useSelector(
    state => state.Login
  )
  const branchSubPermissions = getSubPermissions(
    userRole,
    5,
    "branchLocations_3",
    accountType,
    accountScope
  )
  // Separate pickup text only matters when the plan has delivery and at least
  // one collect-at-branch type; otherwise one text covers every order.
  const hasPickup = isFulfillmentSubPermissionEnabled(
    branchSubPermissions,
    "enablePickup"
  )
  const hasCurbside = isFulfillmentSubPermissionEnabled(
    branchSubPermissions,
    "enableCurbside"
  )
  const hasOrderTypeVariants =
    isFulfillmentSubPermissionEnabled(branchSubPermissions, "enableDelivery") &&
    (hasPickup || hasCurbside)
  // Name only the collect-at-branch types the plan allows.
  const collectionText =
    hasPickup && hasCurbside
      ? {
          title: "Pickup / Curbside text",
          hint: "Optional. Used for pickup and curbside orders in the notification and live order card. Leave empty to use the text above.",
        }
      : hasCurbside
      ? {
          title: "Curbside text",
          hint: "Optional. Used for curbside orders in the notification and live order card. Leave empty to use the text above.",
        }
      : {
          title: "Pickup text",
          hint: "Optional. Used for pickup orders in the notification and live order card. Leave empty to use the text above.",
        }
  const isOrderStatus =
    pushNotificationDetails?.type === ORDER_STATUS_TYPE && hasOrderTypeVariants

  useEffect(() => {
    const fetchOldDetails = () => {
      const payload = {
        id,
      }
      dispatch(getPushNotificationDetails(payload))
    }
    fetchOldDetails()
  }, [])

  useEffect(() => {
    if (pushNotificationDetails) {
      const { title, body, status, remark, variants } = pushNotificationDetails
      const pickup = variants?.pickup

      setFormValues(prevVal => ({
        ...prevVal,
        titleEn: title?.en,
        titleAr: title?.ar,
        bodyEn: body?.en,
        bodyAr: body?.ar,
        remark,
        status,
        pickupTitleEn: pickup?.title?.en || "",
        pickupTitleAr: pickup?.title?.ar || "",
        pickupBodyEn: pickup?.body?.en || "",
        pickupBodyAr: pickup?.body?.ar || "",
      }))
    }
  }, [pushNotificationDetails])

  const handleOnSubmit = values => {
    const {
      titleEn,
      titleAr,
      bodyEn,
      bodyAr,
      remark,
      status,
      pickupTitleEn,
      pickupTitleAr,
      pickupBodyEn,
      pickupBodyAr,
    } = values

    const payload = {
      id,
      title: {
        en: titleEn,
        ar: titleAr,
      },
      body: {
        en: bodyEn,
        ar: bodyAr,
      },
      allowedCustomSound: false, //set default false  //field should not be show on form
      sound: "default", // set default value as "default" //field should not be show on form
      remark: remark ? remark : "",
      status: status ? 1 : 0,
    }
    if (isOrderStatus) {
      payload.variants = {
        pickup: {
          title: { en: pickupTitleEn || "", ar: pickupTitleAr || "" },
          body: { en: pickupBodyEn || "", ar: pickupBodyAr || "" },
        },
      }
    }

    dispatch(updatePushNotification(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          navigate("/app-notifications")
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  if (loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col lg={12} className="d-block mx-auto">
            <Card>
              <CardBody>
                <CardTitle className="d-flex align-items-center">
                  <BackButton />
                  {t("Edit App Notification")}
                </CardTitle>
                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  validationSchema={editNotificationSchema()}
                  validateOnBlur
                  validateOnChange={false}
                  onSubmit={handleOnSubmit}
                >
                  {({ values, errors, touched, setFieldValue }) => (
                    <Form>
                      <Row className="mb-4">
                        {isOrderStatus && (
                          <Col md={12}>
                            <h6 className="mb-0">
                              {t("Delivery / default text")}
                            </h6>
                          </Col>
                        )}
                        <Col md={6}>
                          <div className="form_field">
                            <label
                              htmlFor="titleEn"
                              className="col col-form-label"
                            >
                              <span className="text-danger">* </span>
                              {t("Title(English)")}
                            </label>
                            <Field
                              name="titleEn"
                              type="text"
                              id="titleEn"
                              maxLength={255}
                              placeholder={t("Title(English)")}
                              className={`form-control ${
                                touched.titleEn && errors.titleEn
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="titleEn" />
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="form_field">
                            <label
                              htmlFor="titleAr"
                              className="col col-form-label"
                            >
                              <span className="text-danger">* </span>
                              {t("Title(عربي)")}
                            </label>
                            <Field
                              name="titleAr"
                              type="text"
                              id="titleAr"
                              maxLength={255}
                              style={{ direction: "rtl" }}
                              placeholder={t("Title(عربي)")}
                              className={`form-control ${
                                touched.titleAr && errors.titleAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="titleAr" />
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="form_field">
                            <label
                              htmlFor="bodyEn"
                              className="col col-form-label"
                            >
                              <span className="text-danger">* </span>
                              {t("Message(English)")}
                            </label>
                            <Field
                              as="textarea"
                              id="bodyEn"
                              name="bodyEn"
                              rows={3}
                              placeholder={t("Message(English)")}
                              className={`form-control ${
                                touched.bodyEn && errors.bodyEn
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="bodyEn" />
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="form_field">
                            <label
                              htmlFor="bodyAr"
                              className="col col-form-label"
                            >
                              <span className="text-danger">* </span>
                              {t("Message(عربي)")}
                            </label>
                            <Field
                              as="textarea"
                              id="bodyAr"
                              name="bodyAr"
                              rows={3}
                              placeholder={t("Message(عربي)")}
                              style={{ direction: "rtl" }}
                              className={`form-control ${
                                touched.bodyAr && errors.bodyAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="bodyAr" />
                          </div>
                        </Col>

                        {isOrderStatus && (
                          <>
                            <Col md={12} className="mt-3">
                              <h6 className="mb-1">
                                {t(collectionText.title)}
                              </h6>
                              <p className="text-muted small mb-0">
                                {t(collectionText.hint)}
                              </p>
                            </Col>
                            {PICKUP_FIELDS.map(field => (
                              <Col md={6} key={field.name}>
                                <div className="form_field">
                                  <label
                                    htmlFor={field.name}
                                    className="col col-form-label"
                                  >
                                    {t(field.label)}
                                  </label>
                                  <Field
                                    {...(field.textarea
                                      ? { as: "textarea", rows: 3 }
                                      : { type: "text", maxLength: 255 })}
                                    name={field.name}
                                    id={field.name}
                                    placeholder={t(field.label)}
                                    style={
                                      field.rtl ? { direction: "rtl" } : undefined
                                    }
                                    className="form-control"
                                  />
                                </div>
                              </Col>
                            ))}
                          </>
                        )}

                        <Col md={6}>
                          <div className="form_field">
                            <label
                              htmlFor="remark"
                              className="col col-form-label"
                            >
                              {t("Remark")}
                            </label>
                            <Field
                              name="remark"
                              type="text"
                              id="remark"
                              placeholder={t("Remark")}
                              className={`form-control `}
                            />
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="form_field">
                            <label htmlFor="">{t("Status")}</label>
                            <Switch
                              isChecked={values.status}
                              onClickHandler={() => {
                                setFieldValue("status", !values.status)
                              }}
                            />
                          </div>
                        </Col>
                        <div className="col-12 mt-4">
                          <div className="d-flex align-items-center justify-content-end">
                            <Button
                              type="button"
                              color="secondary"
                              className=" mx-2"
                              onClick={() => navigate(-1)}
                            >
                              {t("Cancel")}
                            </Button>
                            <Button
                              color="success"
                              type="submit"
                              style={{ width: "fit-content" }}
                              disabled={isLoading}
                            >
                              {t("Save")}
                            </Button>
                          </div>
                        </div>
                      </Row>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default EditAppNotification
